import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";
import {
  Question,
  ExerciseOptionTypes,
  ExerciseCategoryData,
  ExerciseGroupingsItem,
  ExerciseMatchingOptionsItem,
} from "@/types";
import GapFillExercise, {
  QuestionData,
} from "@/components/exercises/grammar/Desktop/GapFillExercise";
import GroupingExercise from "@/components/exercises/grammar/Desktop/GroupingExercise";
import GapFillExerciseMobile from "@/components/exercises/grammar/Mobile/GapFillExerciseMobile";
import GroupingExerciseMobile from "@/components/exercises/grammar/Mobile/GroupingExerciseMobile";
import MatchingOptionExercise from "@/components/exercises/grammar/Desktop/MatchingOptionExercise";
import MultipleChoiceExercise from "@/components/exercises/grammar/Desktop/MultipleChoiceExercise";
import MatchingOptionExerciseMobile from "@/components/exercises/grammar/Mobile/MatchingOptionExerciseMobile";
import MultipleChoiceExerciseMobile from "@/components/exercises/grammar/Mobile/MultipleChoiceExerciseMobile";

interface IProps {
  isSmall: boolean;
  exerciseData: ExerciseCategoryData[] | null;
  currentExerciseType: ExerciseOptionTypes;
}

// // --- Type guards ---
// const isQuestionData = (data: ExerciseCategoryData[]): data is Question[] =>
//   data.every((item) => "question_type" in item && "correct_answer" in item);

const isQuestionData = (data: ExerciseCategoryData[]): data is Question[] =>
  data.every((item) =>
    "type" in item &&
    "question" in item &&
    "options" in item &&
    Array.isArray(item.options)
  );


const isMatchingOptionData = (
  data: ExerciseCategoryData[],
): data is ExerciseMatchingOptionsItem[] =>
  data.every(
    (item) => "statement" in item && "correct_answer" in item && "type" in item,
  );

const isGapFillData = (
  data: ExerciseCategoryData[],
): data is QuestionData[] =>
  data.every(
    (item) =>
      "id" in item &&
      "statement" in item &&
      "correct_answer" in item
  );

const isGroupingData = (
  data: ExerciseCategoryData[],
): data is ExerciseGroupingsItem[] =>
  data.every((item) => "id" in item && "category" in item && "word" in item);

// --- Component ---
const RenderExerciseComponent = ({
  isSmall,
  exerciseData,
  currentExerciseType,
}: IProps) => {
  if (!exerciseData) {
    return (
      <div className="mt-4 text-center">
        No data available for this exercise type.
      </div>
    );
  }

  switch (currentExerciseType) {
    case ExerciseOptionTypes.MultipleChoice:
    case ExerciseOptionTypes.TrueFalse:
      if (isQuestionData(exerciseData)) {
        return isSmall ? (
          <MultipleChoiceExerciseMobile
            questions={exerciseData}
            exerciseType={currentExerciseType}
          />
        ) : (
          <MultipleChoiceExercise
            questions={exerciseData}
            exerciseType={currentExerciseType}
          />
        );
      }
      break;

    case ExerciseOptionTypes.MatchingOption:
      if (isMatchingOptionData(exerciseData)) {
        return isSmall ? (
          <DndProvider
            backend={TouchBackend}
            options={{ enableMouseEvents: true }}
          >
            <MatchingOptionExerciseMobile data={exerciseData} />
          </DndProvider>
        ) : (
          <MatchingOptionExercise data={exerciseData} />
        );
      }
      break;

    case ExerciseOptionTypes.GapFill:
      if (isGapFillData(exerciseData)) {
        return isSmall ? (
          <GapFillExerciseMobile questions={exerciseData} />
        ) : (
          <GapFillExercise questions={exerciseData} />
        );
      }
      break;

    case ExerciseOptionTypes.Grouping:
      if (isGroupingData(exerciseData)) {
        return isSmall ? (
          <GroupingExerciseMobile data={exerciseData} />
        ) : (
          <GroupingExercise data={exerciseData} />
        );
      } else {
        console.error("Invalid data format for Grouping exercise.");
        return <div>Invalid data format for exercise grouping.</div>;
      }

    default:
      return (
        <div className="mt-4 text-center">
          This exercise version is not found into platform.
        </div>
      );
  }

  return (
    <div className="mt-4 text-center">
      This exercise version is not supported with current data.
    </div>
  );
};

export default RenderExerciseComponent;
